// Help centre & lessons (owner request 14 Oct 2026).
//   HelpView      /help — searchable articles by topic, keyboard shortcuts, what's new
//   HelpPanel     the «?» panel: help for the screen you are on (top bar, every space's tool row, the «?» key)
//   LessonsView   /lessons[/<slug>] — 12 short lessons: steps, a video per language (or «coming soon»), a short quiz,
//                 progress synced with the workspace (setting `lessons.progress`)
// Texts come from guide/articles.ts and guide/lessons.ts (el / en / ru); a [[key]] inside them is the live label of a
// button or screen, so the help always names what the advisor actually sees.
import { Fragment, useEffect, useMemo, useState } from 'react';
import { lessonProgress, saveLessonProgress } from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Modal, PageHead, Seg } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, useRoute } from '../core/router.ts';
import { WhatsNew } from './whats-new.tsx';
import { HELP_ARTICLES, HELP_GROUPS, SHORTCUTS, articlesForModule, searchArticles, type HelpArticle } from '../guide/articles.ts';
import { LESSONS, lessonBySlug, type Lesson } from '../guide/lessons.ts';
import { lang3, type L3 } from '../guide/l3.ts';
import { lessonVideo, parseVideoManifest, type LessonVideoMap } from '../guide/videos.ts';
import { AskHelp } from './help-assistant.tsx';

/** text with [[key]] labels rendered as the live UI label */
export function GuideText({ text }: { text: string }) {
  const rt = useRuntime();
  const parts = text.split(/(\[\[[a-z0-9_.]+\]\])/gi);
  return <>{parts.map((p, i) => { const m = /^\[\[([a-z0-9_.]+)\]\]$/i.exec(p); return m ? <b key={i} className="ui-label">{rt.t(m[1]!)}</b> : <Fragment key={i}>{p}</Fragment>; })}</>;
}
const useL = () => { const rt = useRuntime(); const l = lang3(rt.locale); return (x: L3) => x[l]; };

function ArticleBody({ a, compact }: { a: HelpArticle; compact?: boolean }) {
  const rt = useRuntime();
  const L = useL();
  return (
    <article className="guide-article" data-testid={`help-article-${a.id}`} aria-labelledby={`ha-${a.id}`}>
      {!compact && <h2 id={`ha-${a.id}`}>{L(a.title)}</h2>}
      <p className="guide-summary">{L(a.summary)}</p>
      {a.sections.map((s, i) => (
        <section key={i} className="guide-section">
          {s.h && <h3>{L(s.h)}</h3>}
          {s.p && <p><GuideText text={L(s.p)} /></p>}
          {s.steps && <ol className="guide-steps">{s.steps.map((x, j) => <li key={j}><GuideText text={L(x)} /></li>)}</ol>}
          {s.tip && <p className="callout small guide-tip"><Icon name="spark" size={14} /> <GuideText text={L(s.tip)} /></p>}
        </section>
      ))}
      {(a.open?.length || a.lesson) && <div className="row wrap guide-actions">
        {a.open?.map((o) => <button key={o.path} className="btn btn-sm" onClick={() => { rt.close(); navigate(o.path); }}><Icon name="arrow" size={14} />{rt.t(o.label)}</button>)}
        {a.lesson && <button className="btn btn-sm btn-ghost" onClick={() => { rt.close(); navigate(`/lessons/${a.lesson}`); }} data-testid="help-to-lesson"><Icon name="presentation" size={14} />{rt.t('help.lesson_link', { title: L(lessonBySlug(a.lesson)!.title) })}</button>}
      </div>}
    </article>
  );
}

function ArticleList({ items, onPick, current }: { items: HelpArticle[]; onPick: (a: HelpArticle) => void; current?: string | null }) {
  const L = useL();
  return (
    <ul className="guide-list" role="list">
      {items.map((a) => (
        <li key={a.id}><button type="button" className="guide-item" aria-current={current === a.id ? 'true' : undefined} onClick={() => onPick(a)} data-testid={`help-item-${a.id}`}>
          <span className="guide-item-icon" aria-hidden="true"><Icon name={a.icon} size={16} /></span>
          <span className="grow"><b>{L(a.title)}</b><span className="tiny muted">{L(a.summary)}</span></span>
        </button></li>
      ))}
    </ul>
  );
}

/** the «?» panel: help for the current screen + search */
export function HelpPanel({ moduleId }: { moduleId?: string }) {
  const rt = useRuntime();
  const { t } = rt;
  const lang = lang3(rt.locale);
  const ctx = useMemo(() => (moduleId ? articlesForModule(moduleId) : []), [moduleId]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState<HelpArticle | null>(ctx[0] ?? null);
  const hits = useMemo(() => (q.trim() ? searchArticles(q, lang, (k) => t(k)) : []), [q, lang]);
  return (
    <Modal wide title={<span className="row" style={{ gap: 8 }}><Icon name="spark" />{t('help.panel_title')}</span>} footer={<>
      <button className="btn btn-ghost" onClick={() => { rt.close(); navigate('/lessons'); }}><Icon name="presentation" />{t('nav.lessons')}</button>
      <span className="spacer" />
      <button className="btn" onClick={() => { rt.close(); navigate(open ? `/help?a=${open.id}` : '/help'); }} data-testid="help-open-centre">{t('help.open_centre')}</button>
    </>}>
      <div className="stack" style={{ gap: 12 }} data-testid="help-panel">
        <input className="input" type="search" value={q} onChange={(e: { target: HTMLInputElement }) => setQ(e.target.value)} placeholder={t('help.search')} aria-label={t('help.search')} data-testid="help-panel-search" />
        {q.trim() ? (hits.length ? <ArticleList items={hits.slice(0, 8)} onPick={(a) => { setOpen(a); setQ(''); }} /> : <p className="muted small" style={{ margin: 0 }}>{t('help.no_results', { q })}</p>)
          : open ? <>
            {ctx.length > 1 && <div className="row wrap" style={{ gap: 6 }}>{ctx.map((a) => <button key={a.id} className="chip toggle" aria-pressed={open.id === a.id} onClick={() => setOpen(a)}>{a.title[lang]}</button>)}</div>}
            <h2 className="guide-panel-title">{open.title[lang]}</h2>
            <ArticleBody a={open} compact />
          </> : <><p className="muted small" style={{ margin: 0 }}>{t('help.no_context')}</p><ArticleList items={HELP_ARTICLES.slice(0, 6)} onPick={setOpen} /></>}
      </div>
    </Modal>
  );
}
export const openHelp = (rt: Runtime, moduleId?: string) => rt.open(<HelpPanel moduleId={moduleId} />);

/** /help — the help centre */
export function HelpView() {
  const rt = useRuntime();
  const { t } = rt;
  const route = useRoute();
  const lang = lang3(rt.locale);
  const L = useL();
  const [tab, setTab] = useState<'articles' | 'ask' | 'shortcuts' | 'new'>(() => { const q = route.query.get('tab'); return q === 'shortcuts' || q === 'new' || q === 'ask' ? q : 'articles'; });
  const [q, setQ] = useState('');
  const asked = route.query.get('tab');
  useEffect(() => { if (asked === 'ask' || asked === 'new' || asked === 'shortcuts') setTab(asked); }, [asked]);
  const current = route.query.get('a');
  const article = current ? HELP_ARTICLES.find((a) => a.id === current) ?? null : null;
  const hits = useMemo(() => (q.trim() ? searchArticles(q, lang, (k) => t(k)) : null), [q, lang]);
  const pick = (a: HelpArticle) => navigate(`/help?a=${a.id}`, { replace: !!current });
  return (
    <div data-testid="help-centre">
      <PageHead title={t('help.title')} subtitle={t('help.subtitle')} actions={<>
        <button className="btn" onClick={() => navigate('/lessons')} data-testid="help-lessons"><Icon name="presentation" />{t('nav.lessons')}</button>
        <button className="btn btn-ghost" onClick={() => navigate('/setup')}><Icon name="check" />{t('nav.setup')}</button>
      </>} />
      <div className="row wrap" style={{ gap: 10, marginBottom: 16 }}>
        <Seg values={['articles', 'ask', 'shortcuts', 'new'] as const} value={tab} onChange={setTab} label={(x) => t(`help.tab.${x}`)} testId="help-tabs" />
        {tab === 'articles' && <input className="input grow" style={{ maxWidth: 420 }} type="search" value={q} onChange={(e: { target: HTMLInputElement }) => setQ(e.target.value)} placeholder={t('help.search')} aria-label={t('help.search')} data-testid="help-search" />}
      </div>
      {tab === 'articles' && <div className="guide-layout">
        <nav className="card guide-nav" aria-label={t('help.topics')}>
          {hits ? (hits.length ? <ArticleList items={hits} onPick={pick} current={current} /> : <p className="muted small" style={{ padding: 12, margin: 0 }} data-testid="help-no-results">{t('help.no_results', { q })}</p>)
            : HELP_GROUPS.map((g) => { const items = HELP_ARTICLES.filter((a) => a.group === g); return items.length ? <div key={g}><div className="upper guide-group">{t(`help.group.${g}`)}</div><ArticleList items={items} onPick={pick} current={current} /></div> : null; })}
        </nav>
        <div className="card card-pad guide-main">
          {article ? <ArticleBody a={article} /> : <div className="stack" style={{ gap: 14 }}>
            <h2 style={{ margin: 0 }}>{t('help.welcome_title')}</h2>
            <p className="muted" style={{ margin: 0 }}>{t('help.welcome_sub')}</p>
            <div className="guide-tiles">
              {['start-here', 'setup-wizard', 'import-clients', 'today', 'sale-file', 'backups'].map((id) => { const a = HELP_ARTICLES.find((x) => x.id === id)!; return (
                <button key={id} className="guide-tile" onClick={() => pick(a)}><Icon name={a.icon} /><b>{L(a.title)}</b><span className="tiny muted">{L(a.summary)}</span></button>); })}
            </div>
            <p className="tiny muted" style={{ margin: 0 }}>{t('help.press_question')}</p>
          </div>}
        </div>
      </div>}
      {tab === 'ask' && <section className="card card-pad" data-testid="help-ask"><AskHelp /></section>}
      {tab === 'shortcuts' && <section className="card card-pad" data-testid="help-shortcuts">
        <h2 style={{ marginTop: 0 }}>{t('help.tab.shortcuts')}</h2>
        <table className="table guide-keys"><thead><tr><th>{t('help.keys')}</th><th>{t('help.action')}</th><th>{t('help.where')}</th></tr></thead>
          <tbody>{SHORTCUTS.map((s, i) => <tr key={i}><td>{s.keys.map((k, j) => <Fragment key={j}>{j > 0 && ' '}<kbd className="kbd">{k}</kbd></Fragment>)}</td><td>{L(s.what)}</td><td className="muted small">{L(s.where)}</td></tr>)}</tbody></table>
      </section>}
      {tab === 'new' && <WhatsNew />}
    </div>
  );
}

// --- lessons -------------------------------------------------------------------------------------------------------
let MANIFEST: Promise<LessonVideoMap> | null = null;
function useVideoManifest(): LessonVideoMap {
  const [m, setM] = useState<LessonVideoMap>({});
  useEffect(() => {
    MANIFEST ??= (typeof fetch === 'function' && location.protocol !== 'file:'
      ? fetch('lessons/videos.json', { cache: 'no-cache' }).then((r) => (r.ok ? r.json() : {})).then(parseVideoManifest).catch(() => ({}))
      : Promise.resolve({}));
    let on = true; void MANIFEST.then((x) => { if (on) setM(x); }); return () => { on = false; };
  }, []);
  return m;
}

export function LessonsView({ slug }: { slug?: string }) {
  const rt = useRuntime();
  const { t } = rt;
  const L = useL();
  const progress = lessonProgress(rt.view);
  const lesson = slug ? lessonBySlug(slug) : null;
  if (lesson) return <LessonDetail lesson={lesson} />;
  const done = LESSONS.filter((l) => progress[l.slug]?.doneAt).length;
  const pct = Math.round((done / LESSONS.length) * 100);
  const next = LESSONS.find((l) => !progress[l.slug]?.doneAt);
  return (
    <div data-testid="lessons">
      <PageHead title={t('lessons.title')} subtitle={t('lessons.subtitle', { count: LESSONS.length })} actions={<button className="btn" onClick={() => navigate('/help')}><Icon name="spark" />{t('nav.help')}</button>} />
      <section className="card card-pad lessons-hero">
        <div className="grow">
          <div className="row spread"><b>{t('lessons.progress', { done, total: LESSONS.length })}</b><span className="num">{pct}%</span></div>
          <div className="meter lvl-3" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t('lessons.progress', { done, total: LESSONS.length })}><i style={{ width: `${pct}%` }} /></div>
        </div>
        {next && <button className="btn btn-primary" onClick={() => navigate(`/lessons/${next.slug}`)} data-testid="lessons-continue"><Icon name="arrow" />{t(done ? 'lessons.continue' : 'lessons.start', { title: L(next.title) })}</button>}
      </section>
      <div className="lesson-grid">
        {LESSONS.map((l, i) => { const p = progress[l.slug]; return (
          <button key={l.slug} className={`lesson-card${p?.doneAt ? ' done' : ''}`} onClick={() => navigate(`/lessons/${l.slug}`)} data-testid={`lesson-${l.slug}`}>
            <span className="lesson-num" aria-hidden="true">{p?.doneAt ? <Icon name="check" size={16} /> : i + 1}</span>
            <span className="grow stack" style={{ gap: 4 }}>
              <b>{L(l.title)}</b>
              <span className="tiny muted">{L(l.goal)}</span>
              <span className="row wrap" style={{ gap: 6 }}>
                <span className="chip">{t('lessons.minutes', { n: l.minutes })}</span>
                <span className="chip">{t(`lessons.level.${l.level}`)}</span>
                {p?.doneAt && <span className="chip ok">{t('lessons.done')}</span>}
                {p?.of ? <span className="chip gold">{t('lessons.quiz_score', { correct: p.quiz ?? 0, of: p.of })}</span> : null}
              </span>
            </span>
          </button>); })}
      </div>
    </div>
  );
}

function LessonVideo({ lesson }: { lesson: Lesson }) {
  const rt = useRuntime();
  const { t } = rt;
  const manifest = useVideoManifest();
  const lang = lang3(rt.locale);
  const [other, setOther] = useState<typeof lang | null>(null);
  const pick = lessonVideo(lesson.videoId, other ?? lang, manifest);
  const src = pick.source;
  if (src) return (
    <div className="lesson-video" data-testid="lesson-video">
      <video controls preload="metadata" poster={src.poster ?? undefined} aria-label={lesson.title[lang]} key={src.src}>
        <source src={src.src} />
        {src.captions && <track kind="captions" src={src.captions} srcLang={pick.lang} label={pick.lang.toUpperCase()} default />}
      </video>
    </div>
  );
  const elsewhere = pick.available.filter((l) => l !== lang);
  return (
    <div className="lesson-video soon" data-testid="lesson-video-soon" role="note">
      <span className="lesson-play" aria-hidden="true"><Icon name="presentation" size={26} /></span>
      <b>{t('lessons.video_soon')}</b>
      <span className="small muted">{t('lessons.video_soon_sub')}</span>
      {elsewhere.length > 0 && <span className="row wrap" style={{ gap: 6, justifyContent: 'center' }}>{elsewhere.map((l) => <button key={l} className="btn btn-sm" onClick={() => setOther(l)}>{t('lessons.watch_in', { lang: t(`lessons.lang.${l}`) })}</button>)}</span>}
      <code className="tiny muted">{lesson.videoId}</code>
    </div>
  );
}

function LessonDetail({ lesson }: { lesson: Lesson }) {
  const rt = useRuntime();
  const { t } = rt;
  const L = useL();
  const p = lessonProgress(rt.view)[lesson.slug];
  const idx = LESSONS.findIndex((l) => l.slug === lesson.slug);
  const prev = LESSONS[idx - 1], next = LESSONS[idx + 1];
  useEffect(() => { if (!p?.seenAt && !rt.ws.readOnlyReason) void rt.ws.run((v, c) => saveLessonProgress(v, lesson.slug, { seen: true }, c)).catch(() => undefined); }, [lesson.slug]);
  const done = !!p?.doneAt;
  const toggle = () => rt.run((v, c) => saveLessonProgress(v, lesson.slug, { done: !done }, c), done ? 'lessons.marked_undone' : 'lessons.marked_done');
  return (
    <div data-testid={`lesson-detail-${lesson.slug}`}>
      <PageHead title={L(lesson.title)} subtitle={<span className="row wrap" style={{ gap: 6 }}><span>{t('lessons.of', { n: idx + 1, total: LESSONS.length })}</span><span className="chip">{t('lessons.minutes', { n: lesson.minutes })}</span>{done && <span className="chip ok">{t('lessons.done')}</span>}</span>}
        actions={<button className="btn btn-ghost" onClick={() => navigate('/lessons')}><Icon name="back" />{t('lessons.all')}</button>} />
      <div className="lesson-layout">
        <div className="stack" style={{ gap: 16 }}>
          <LessonVideo lesson={lesson} />
          <section className="card card-pad">
            <div className="upper">{t('lessons.goal')}</div>
            <p style={{ marginTop: 4 }}>{L(lesson.goal)}</p>
            <div className="upper">{t('lessons.steps')}</div>
            <ol className="guide-steps big">{lesson.steps.map((s, i) => <li key={i}><GuideText text={L(s)} /></li>)}</ol>
            {lesson.tips?.map((x, i) => <p key={i} className="callout small guide-tip"><Icon name="spark" size={14} /> <GuideText text={L(x)} /></p>)}
            <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
              {lesson.practice && <button className="btn" onClick={() => navigate(lesson.practice!.path)} data-testid="lesson-practice"><Icon name="arrow" />{t('lessons.practice', { screen: t(lesson.practice.label) })}</button>}
              <button className={`btn ${done ? 'btn-ghost' : 'btn-primary'}`} onClick={() => void toggle()} data-testid="lesson-done">{done ? t('lessons.mark_undone') : t('lessons.mark_done')}</button>
            </div>
          </section>
          <Quiz lesson={lesson} />
        </div>
        <aside className="stack" style={{ gap: 12 }}>
          <section className="card card-pad">
            <div className="upper">{t('lessons.read_more')}</div>
            <ul className="plain-list guide-related">{lesson.articles.map((id) => { const a = HELP_ARTICLES.find((x) => x.id === id); return a ? <li key={id}><button className="linklike" onClick={() => navigate(`/help?a=${id}`)}>{L(a.title)}</button></li> : null; })}</ul>
          </section>
          <nav className="row spread" aria-label={t('lessons.nav')}>
            {prev ? <button className="btn btn-sm btn-ghost" onClick={() => navigate(`/lessons/${prev.slug}`)}><Icon name="back" size={14} />{t('lessons.prev')}</button> : <span />}
            {next && <button className="btn btn-sm" onClick={() => navigate(`/lessons/${next.slug}`)} data-testid="lesson-next">{t('lessons.next')}<Icon name="arrow" size={14} /></button>}
          </nav>
        </aside>
      </div>
    </div>
  );
}

function Quiz({ lesson }: { lesson: Lesson }) {
  const rt = useRuntime();
  const { t } = rt;
  const L = useL();
  const [answers, setAnswers] = useState<(number | null)[]>(() => lesson.quiz.map(() => null));
  const [checked, setChecked] = useState(false);
  useEffect(() => { setAnswers(lesson.quiz.map(() => null)); setChecked(false); }, [lesson.slug]);
  if (!lesson.quiz.length) return null;
  const correct = lesson.quiz.filter((q, i) => answers[i] === q.answer).length;
  const check = async () => {
    setChecked(true);
    await rt.run((v, c) => saveLessonProgress(v, lesson.slug, { quiz: { correct, of: lesson.quiz.length }, ...(correct === lesson.quiz.length ? { done: true } : {}) }, c));
  };
  return (
    <section className="card card-pad" data-testid="lesson-quiz" aria-labelledby={`quiz-${lesson.slug}`}>
      <h3 id={`quiz-${lesson.slug}`} style={{ marginTop: 0 }}>{t('lessons.quiz')}</h3>
      <div className="stack" style={{ gap: 14 }}>
        {lesson.quiz.map((q, i) => (
          <fieldset key={i} className="quiz-q">
            <legend><b>{i + 1}. {L(q.q)}</b></legend>
            {q.options.map((o, j) => {
              const state = checked ? (j === q.answer ? 'right' : answers[i] === j ? 'wrong' : '') : '';
              return <label key={j} className={`quiz-opt ${state}`}><input type="radio" name={`q-${lesson.slug}-${i}`} checked={answers[i] === j} disabled={checked} onChange={() => setAnswers(Object.assign([...answers], { [i]: j }))} />{L(o)}</label>;
            })}
            {checked && <p className={`small quiz-why ${answers[i] === q.answer ? 'ok-text' : 'warn-text'}`} role="status">{answers[i] === q.answer ? t('lessons.right') : t('lessons.wrong')} {L(q.why)}</p>}
          </fieldset>
        ))}
      </div>
      <div className="row wrap" style={{ gap: 8, marginTop: 12 }}>
        {!checked ? <button className="btn btn-primary" disabled={answers.some((a) => a === null)} onClick={() => void check()} data-testid="quiz-check">{t('lessons.check')}</button>
          : <><b className={correct === lesson.quiz.length ? 'ok-text' : ''} data-testid="quiz-result">{t('lessons.quiz_score', { correct, of: lesson.quiz.length })}</b><button className="btn btn-ghost" onClick={() => { setAnswers(lesson.quiz.map(() => null)); setChecked(false); }}>{t('lessons.retry')}</button></>}
      </div>
    </section>
  );
}

/** small «?» buttons: in the top bar and at the end of each space's tool row */
export function HelpButton({ moduleId, variant }: { moduleId: string; variant: 'top' | 'space' }) {
  const rt = useRuntime();
  return variant === 'top'
    ? <button className="btn btn-icon" aria-label={rt.t('help.open')} title={`${rt.t('help.open')} (?)`} onClick={() => openHelp(rt, moduleId)} data-testid="topbar-help"><span className="help-q" aria-hidden="true">?</span></button>
    : <button className="spacenav-help" aria-label={rt.t('help.this_screen')} title={rt.t('help.this_screen')} onClick={() => openHelp(rt, moduleId)} data-testid="space-help"><span aria-hidden="true">?</span></button>;
}
